import { useEffect, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { AVATAR_IDS, AVATARS, PAINT_IDS, PAINTS, SUIT_IDS, SUITS, avatarFile, type ArmorChoice } from '../game/armor';
import { savePrefs, useGame } from '../game/store';
import { Armor } from '../render/Armor';

// Development only: ?front stops the turntable facing you; ?head frames the helmet.
const DEV_QUERY = import.meta.env.DEV ? new URLSearchParams(location.search) : null;

function HeadCam() {
  const camera = useThree((s) => s.camera);
  useEffect(() => camera.lookAt(0, 0.62, 0), [camera]);
  return null;
}

function Turntable({ armor }: { armor: ArmorChoice }) {
  const g = useRef<THREE.Group>(null);
  useFrame((_, dt) => {
    if (g.current && !DEV_QUERY?.has('front')) g.current.rotation.y += dt * 0.6;
  });
  return (
    <group ref={g} position={[0, 0.15, 0]} rotation={[0, Math.PI, 0]}>
      <Armor armor={armor} side={0xffb000} />
    </group>
  );
}

/** Choose a suit and a paint, with a turning preview. Strips always glow in your side colour. */
export function ArmorPicker({ onClose }: { onClose: () => void }) {
  const saved = useGame((s) => s.armor);
  const [armor, setArmor] = useState<ArmorChoice>(saved);
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    close.current?.focus();
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  const save = () => {
    useGame.setState({ armor });
    savePrefs();
    onClose();
  };
  const hex = (n: number | null) => (n === null ? 'linear-gradient(135deg,#3a3f47,#23272e)' : `#${n.toString(16).padStart(6, '0')}`);
  return (
    <div className="overlay notes-overlay" role="dialog" aria-modal="true" aria-labelledby="armor-title" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card armor-card">
        <div className="eyebrow">Your look</div>
        <h2 id="armor-title">
          {AVATARS[armor.avatar].name} · {SUITS[armor.suit].name} · {PAINTS[armor.paint].name}
        </h2>
        <div className="armor-preview" aria-hidden="true">
          <Canvas camera={DEV_QUERY?.has('head') ? { position: [0, 0.62, 1.1], fov: 30 } : { position: [0, -0.1, 3.0], fov: 40 }} dpr={[1, 2]}>
            <ambientLight intensity={1.1} />
            <directionalLight position={[2, 3, 3]} intensity={2.4} />
            <directionalLight position={[-3, 1, -2]} intensity={0.6} color={0xa9c4db} />
            <Turntable armor={armor} />
            {DEV_QUERY?.has('head') && <HeadCam />}
          </Canvas>
        </div>
        <div className="lbl">Avatar</div>
        <div className="avatars" role="radiogroup" aria-label="Avatar">
          {AVATAR_IDS.map((id) => (
            <button key={id} type="button" role="radio" aria-checked={armor.avatar === id} aria-label={AVATARS[id].name} title={AVATARS[id].name} className={armor.avatar === id ? 'on' : ''} onClick={() => setArmor({ ...armor, avatar: id })}>
              <img src={avatarFile(id)} alt="" width={64} height={73} />
            </button>
          ))}
        </div>
        <div className="lbl">Suit</div>
        <div className="tier armor-suits" role="radiogroup" aria-label="Suit">
          {SUIT_IDS.map((id) => (
            <button key={id} type="button" role="radio" aria-checked={armor.suit === id} aria-pressed={armor.suit === id} onClick={() => setArmor({ ...armor, suit: id })}>
              {SUITS[id].name}
              <small>{SUITS[id].blurb}</small>
            </button>
          ))}
        </div>
        <div className="lbl">Paint</div>
        <div className="swatches" role="radiogroup" aria-label="Paint">
          {PAINT_IDS.map((id) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={armor.paint === id}
              aria-label={PAINTS[id].name}
              title={PAINTS[id].name}
              className={armor.paint === id ? 'on' : ''}
              style={{ background: hex(PAINTS[id].hex) }}
              onClick={() => setArmor({ ...armor, paint: id })}
            />
          ))}
        </div>
        <p className="note">Your avatar shows behind your visor to your rival. Light strips always glow amber for you and ice blue for your rival, whatever the paint, so you can always tell sides apart.</p>
        <div className="row">
          <button ref={close} className="cta" type="button" onClick={save}>
            Wear it
          </button>
          <button className="cta alt" type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
